'use client'

import { useState } from 'react'
import { useCurrency } from '@/lib/currency'

interface ReceiptData {
  receiptNumber: string
  issueDate: string
  payment: {
    id: string
    amount: number
    currency: string
    status: string
    description: string
    transactionId: string
    stripePaymentIntentId: string
    createdAt: string
    updatedAt: string
  }
  client: {
    id: string
    email: string
    name: string
  }
  landlord: {
    id: string
    email: string
    name: string
  }
  property: {
    id: string
    title: string
    location: string
    price: number
    duration: number
  }
  application: {
    id: string
    leaseDuration: number
    monthlyIncome: number
    employmentStatus: string
    moveInDate: string
    createdAt: string
  }
  platform: {
    name: string
    website: string
    supportEmail: string
    commissionRate: number
    commissionAmount: number
    landlordAmount: number
  }
}

interface PaymentReceiptProps {
  paymentId: string
  clientId?: string // Optional - not required for landlords and admins
  buttonText?: string // Custom button text
  buttonClassName?: string // Custom button styling
}

export default function PaymentReceipt({ 
  paymentId, 
  clientId, 
  buttonText = 'View Receipt',
  buttonClassName = 'px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 font-semibold'
}: PaymentReceiptProps) {
  const [receipt, setReceipt] = useState<ReceiptData | null>(null)
  const [loading, setLoading] = useState(false)
  const [showReceipt, setShowReceipt] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { format } = useCurrency()

  const fetchReceipt = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setError('Please log in to view receipt')
        setLoading(false)
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      // Build URL - client_id is optional now
      const url = clientId 
        ? `${apiUrl}/payments/receipt?id=${paymentId}&client_id=${clientId}`
        : `${apiUrl}/payments/receipt/${paymentId}`
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })
      const data = await response.json()
      
      if (response.ok && data.success) {
        setReceipt(data.receipt)
        setShowReceipt(true)
      } else {
        // Show specific error message from the API
        const errorMessage = data.error || data.message || 'Failed to fetch receipt'
        setError(errorMessage)
        console.error('Receipt API error:', data)
      }
    } catch (error) {
      console.error('Error fetching receipt:', error)
      setError('Network error: Unable to connect to server')
    } finally {
      setLoading(false)
    }
  }

  const downloadReceipt = () => {
    if (!receipt) return

    // Create HTML content for the receipt
         const htmlContent = `
       <!DOCTYPE html>
       <html>
       <head>
         <meta charset="UTF-8">
         <title>Payment Receipt - ${receipt.receiptNumber}</title>
         <style>
           body { 
             font-family: 'DM Sans', system-ui, sans-serif; 
             max-width: 600px; 
             margin: 0 auto; 
             padding: 20px; 
             line-height: 1.6;
             color: #44403c;
             background-color: #ffffff;
           }
           
           .header { 
             text-align: center; 
             border-bottom: 2px solid #e7e5e4; 
             padding-bottom: 20px; 
             margin-bottom: 30px; 
           }
           
           .header h1 {
             font-family: 'DM Serif Display', Georgia, serif;
             color: #1c1917;
             font-size: 28px;
             font-weight: 400;
             margin: 10px 0;
           }
           
           .header p {
             color: #57534e;
             font-size: 14px;
             margin: 5px 0;
           }
           
           .receipt-number { 
             font-size: 24px; 
             font-weight: 600; 
             color: #059669; 
             margin: 10px 0;
           }
           
           .section { 
             margin-bottom: 25px; 
           }
           
           .section-title { 
             font-family: 'DM Serif Display', Georgia, serif;
             font-size: 18px; 
             font-weight: 400; 
             margin-bottom: 10px; 
             color: #1c1917; 
             border-bottom: 2px solid #e7e5e4; 
             padding-bottom: 5px; 
           }
           
           .detail-row { 
             display: flex; 
             justify-content: space-between; 
             margin-bottom: 8px; 
             padding: 4px 0;
             color: #57534e;
           }
           
           .detail-label { 
             font-weight: 600; 
             min-width: 150px;
             color: #1c1917;
           }
           
           .detail-row span:not(.detail-label) {
             color: #57534e;
           }
           
           .total-row { 
             border-top: 2px solid #e7e5e4; 
             padding-top: 10px; 
             font-size: 18px; 
             font-weight: 600; 
             margin-top: 15px;
             color: #1c1917;
           }
           
           .footer { 
             margin-top: 40px; 
             text-align: center; 
             color: #78716c; 
             font-size: 12px; 
             border-top: 1px solid #e7e5e4;
             padding-top: 20px;
           }
           
           .company-info {
             text-align: center;
             margin-bottom: 20px;
             color: #78716c;
           }
           
           .company-info h2 {
             font-family: 'DM Serif Display', Georgia, serif;
             color: #1c1917;
             font-size: 20px;
             font-weight: 400;
             margin: 5px 0;
           }
           
           .amount-highlight {
             background-color: #fafaf9;
             padding: 15px;
             border-radius: 12px;
             border: 1px solid #e7e5e4;
           }
         </style>
       </head>
       <body>
         <div class="header">
           <div class="company-info">
             <h2>LandlordNoAgent</h2>
             <p>Rental Property Management Platform</p>
           </div>
           <h1 style="color: #111827;">PAYMENT RECEIPT</h1>
           <div class="receipt-number">${receipt.receiptNumber}</div>
           <p style="color: #374151;"><strong style="color: #111827;">Date:</strong> ${new Date(receipt.issueDate).toLocaleDateString()} at ${new Date(receipt.issueDate).toLocaleTimeString()}</p>
         </div>

         <div class="section">
           <div class="section-title">Client Information</div>
           <div class="detail-row">
             <span class="detail-label">Full Name:</span>
             <span>${receipt.client.name}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Email Address:</span>
             <span>${receipt.client.email}</span>
           </div>
         </div>

         <div class="section">
           <div class="section-title">Property Information</div>
           <div class="detail-row">
             <span class="detail-label">Property Title:</span>
             <span>${receipt.property?.title || 'N/A'}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Location:</span>
             <span>${receipt.property?.location || 'N/A'}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Monthly Rent:</span>
             <span>${receipt.property?.price ? format(receipt.property.price) : 'N/A'}</span>
           </div>
           ${receipt.property?.duration ? `
           <div class="detail-row">
             <span class="detail-label">Lease Duration:</span>
             <span>${receipt.property.duration} month(s)</span>
           </div>
           ` : ''}
         </div>

         <div class="section">
           <div class="section-title">Payment Details</div>
           <div class="detail-row">
             <span class="detail-label">Description:</span>
             <span>${receipt.payment.description || 'Rental Payment'}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Lease Duration:</span>
             <span style="color: #57534e;">${receipt.application.leaseDuration || receipt.property.duration || 'N/A'} month(s)</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Payment Method:</span>
             <span>STRIPE</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Transaction ID:</span>
             <span>${receipt.payment.transactionId && receipt.payment.transactionId !== 'N/A' ? receipt.payment.transactionId : (receipt.payment.stripePaymentIntentId || 'N/A')}</span>
           </div>
         </div>

         <div class="section">
           <div class="section-title">Amount Breakdown</div>
           <div class="amount-highlight">
             <div class="detail-row total-row">
               <span class="detail-label">Total Paid:</span>
               <span>${format(receipt.payment.amount)}</span>
             </div>
           </div>
         </div>

         <div class="footer">
           <p><strong>Thank you for your payment!</strong></p>
           <p>This is an official receipt for your rental payment.</p>
           <p>Generated on ${new Date().toLocaleString()}</p>
           <p>For support, contact: support@landlordnoagent.com</p>
         </div>
       </body>
       </html>
     `

    // Create and download the file
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `receipt-${receipt.receiptNumber}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const printReceipt = () => {
    if (!receipt) return
    
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Payment Receipt - ${receipt.receiptNumber}</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; color: #1c1917 !important; }
            .no-print { display: none !important; }
            .page-break { page-break-before: always; }
          }
          
          body { 
            font-family: 'DM Sans', system-ui, sans-serif; 
            max-width: 600px; 
            margin: 0 auto; 
            padding: 20px; 
            line-height: 1.6;
            color: #44403c;
            background-color: #ffffff;
          }
          
          .header { 
            text-align: center; 
            border-bottom: 2px solid #e7e5e4; 
            padding-bottom: 20px; 
            margin-bottom: 30px; 
          }
          
          .header h1 {
            font-family: 'DM Serif Display', Georgia, serif;
            color: #1c1917;
            font-size: 28px;
            font-weight: 400;
            margin: 10px 0;
          }
          
          .header p {
            color: #57534e;
            font-size: 14px;
            margin: 5px 0;
          }
          
          .receipt-number { 
            font-size: 24px; 
            font-weight: 600; 
            color: #059669; 
            margin: 10px 0;
          }
          
          .section { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          
          .section-title { 
            font-family: 'DM Serif Display', Georgia, serif;
            font-size: 18px; 
            font-weight: 400; 
            margin-bottom: 10px; 
            color: #1c1917; 
            border-bottom: 2px solid #e7e5e4; 
            padding-bottom: 5px; 
          }
          
          .detail-row { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 8px; 
            padding: 4px 0;
            color: #57534e;
          }
          
          .detail-label { 
            font-weight: 600; 
            min-width: 150px;
            color: #1c1917;
          }
          
          .detail-row span:not(.detail-label) {
            color: #57534e;
          }
          
          .total-row { 
            border-top: 2px solid #e7e5e4; 
            padding-top: 10px; 
            font-size: 18px; 
            font-weight: 600; 
            margin-top: 15px;
            color: #1c1917;
          }
          
          .footer { 
            margin-top: 40px; 
            text-align: center; 
            color: #78716c; 
            font-size: 12px; 
            border-top: 1px solid #e7e5e4;
            padding-top: 20px;
          }
          
          .company-info {
            text-align: center;
            margin-bottom: 20px;
            color: #78716c;
          }
          
          .company-info h2 {
            font-family: 'DM Serif Display', Georgia, serif;
            color: #1c1917;
            font-size: 20px;
            font-weight: 400;
            margin: 5px 0;
          }
          
          .amount-highlight {
            background-color: #fafaf9;
            padding: 15px;
            border-radius: 12px;
            border: 1px solid #e7e5e4;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <h2>LandlordNoAgent</h2>
            <p>Rental Property Management Platform</p>
          </div>
          <h1 style="color: #1c1917; font-family: 'DM Serif Display', Georgia, serif;">Payment Receipt</h1>
          <div class="receipt-number">${receipt.receiptNumber}</div>
          <p style="color: #57534e;"><strong style="color: #1c1917;">Date:</strong> ${new Date(receipt.issueDate).toLocaleDateString()} at ${new Date(receipt.issueDate).toLocaleTimeString()}</p>
        </div>

        <div class="section">
          <div class="section-title">Client Information</div>
          <div class="detail-row">
            <span class="detail-label">Full Name:</span>
            <span>${receipt.client.name}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Email Address:</span>
            <span>${receipt.client.email}</span>
          </div>
        </div>

         <div class="section">
           <div class="section-title">Property Information</div>
           <div class="detail-row">
             <span class="detail-label">Property Title:</span>
             <span>${receipt.property?.title || 'N/A'}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Location:</span>
             <span>${receipt.property?.location || 'N/A'}</span>
           </div>
           <div class="detail-row">
             <span class="detail-label">Monthly Rent:</span>
             <span>${receipt.property?.price ? format(receipt.property.price) : 'N/A'}</span>
           </div>
           ${receipt.property?.duration ? `
           <div class="detail-row">
             <span class="detail-label">Lease Duration:</span>
             <span>${receipt.property.duration} month(s)</span>
           </div>
           ` : ''}
         </div>

        <div class="section">
          <div class="section-title">Payment Details</div>
          <div class="detail-row">
            <span class="detail-label">Description:</span>
            <span>${receipt.payment.description || 'Rental Payment'}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Lease Duration:</span>
            <span style="color: #374151;">${receipt.application.leaseDuration || receipt.property.duration || 'N/A'} month(s)</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment Method:</span>
            <span>STRIPE</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Transaction ID:</span>
            <span>${receipt.payment.transactionId && receipt.payment.transactionId !== 'N/A' ? receipt.payment.transactionId : (receipt.payment.stripePaymentIntentId || 'N/A')}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Amount Breakdown</div>
          <div class="amount-highlight">
            <div class="detail-row total-row">
              <span class="detail-label">Total Paid:</span>
              <span>${format(receipt.payment.amount)}</span>
            </div>
          </div>
        </div>

        <div class="footer">
          <p><strong>Thank you for your payment!</strong></p>
          <p>This is an official receipt for your rental payment.</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>For support, contact: support@landlordnoagent.com</p>
        </div>
      </body>
      </html>
    `)
    
    printWindow.document.close()
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print()
    }, 500)
  }

  return (
    <div className="payment-receipt">
      <button
        onClick={fetchReceipt}
        disabled={loading}
        className={buttonClassName}
      >
        {loading ? 'Loading...' : buttonText}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          <strong>Error:</strong> {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-600 hover:text-red-800 font-semibold rounded p-0.5 hover:bg-red-100 transition-colors"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {showReceipt && receipt && (
        <div className="fixed inset-0 bg-stone-900/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 animate-scale-in">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-display font-normal text-stone-900 tracking-tight">Payment Receipt</h2>
                <button
                  onClick={() => setShowReceipt(false)}
                  className="text-stone-500 hover:text-stone-700 text-2xl font-bold leading-none p-1 rounded-lg hover:bg-stone-100 transition-colors"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-stone-50/50 p-6 mb-6">
                <div className="text-center mb-6 pb-6 border-b border-stone-200">
                  <p className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-1">LandlordNoAgent</p>
                  <h1 className="text-2xl font-display font-normal text-stone-900 mb-2">Payment Receipt</h1>
                  <div className="text-xl font-semibold text-emerald-600 mb-2">{receipt.receiptNumber}</div>
                  <p className="text-stone-600 text-sm">Date: {new Date(receipt.issueDate).toLocaleDateString()} at {new Date(receipt.issueDate).toLocaleTimeString()}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-display font-normal text-stone-900 text-lg mb-3 border-b border-stone-200 pb-2">Client Information</h3>
                    <p className="text-stone-700 text-sm"><span className="font-semibold text-stone-900">Name:</span> {receipt.client.name}</p>
                    <p className="text-stone-700 text-sm mt-1"><span className="font-semibold text-stone-900">Email:</span> {receipt.client.email}</p>
                  </div>

                  <div>
                    <h3 className="font-display font-normal text-stone-900 text-lg mb-3 border-b border-stone-200 pb-2">Property Information</h3>
                    <p className="text-stone-700 text-sm"><span className="font-semibold text-stone-900">Property:</span> {receipt.property?.title || 'N/A'}</p>
                    <p className="text-stone-700 text-sm mt-1"><span className="font-semibold text-stone-900">Location:</span> {receipt.property?.location || 'N/A'}</p>
                    <p className="text-stone-700 text-sm mt-1"><span className="font-semibold text-stone-900">Monthly Rent:</span> {receipt.property?.price ? format(receipt.property.price) : 'N/A'}</p>
                    {receipt.property?.duration && (
                      <p className="text-stone-700 text-sm mt-1"><span className="font-semibold text-stone-900">Lease Duration:</span> {receipt.property.duration} month(s)</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-stone-200">
                  <h3 className="font-display font-normal text-stone-900 text-lg mb-3 border-b border-stone-200 pb-2">Payment Details</h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <p className="text-stone-700"><span className="font-semibold text-stone-900">Description:</span> {receipt.payment.description || 'Rental Payment'}</p>
                    <p className="text-stone-700"><span className="font-semibold text-stone-900">Duration:</span> {receipt.application.leaseDuration || receipt.property.duration || 'N/A'} month(s)</p>
                    <p className="text-stone-700"><span className="font-semibold text-stone-900">Payment Method:</span> STRIPE</p>
                    <p className="text-stone-700"><span className="font-semibold text-stone-900">Transaction ID:</span> {receipt.payment.transactionId && receipt.payment.transactionId !== 'N/A' ? receipt.payment.transactionId : (receipt.payment.stripePaymentIntentId || 'N/A')}</p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-stone-200">
                  <h3 className="font-display font-normal text-stone-900 text-lg mb-3 border-b border-stone-200 pb-2">Amount Breakdown</h3>
                  <div className="bg-white rounded-xl border border-stone-200 p-4 mt-3">
                    <div className="flex justify-between items-center font-semibold text-stone-900">
                      <span>Total Paid</span>
                      <span className="text-emerald-600 text-lg">{format(receipt.payment.amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-center flex-wrap">
                <button
                  onClick={downloadReceipt}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-300 font-semibold hover:shadow-lg hover:shadow-emerald-500/25"
                >
                  Download Receipt
                </button>
                <button
                  onClick={printReceipt}
                  className="px-6 py-3 bg-white text-stone-700 rounded-xl border-2 border-stone-300 hover:border-emerald-600 hover:text-emerald-700 transition-all duration-300 font-semibold"
                >
                  Print Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

